import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Event as EventIcon,
  Assignment as AssignmentIcon,
  Delete as DeleteIcon,
  AccessAlarm as AccessAlarm,
  Notifications,
} from '@mui/icons-material';
import { parseISO, isToday, isPast, isFuture, format } from 'date-fns';
import apiService from '../../services/api';

const Followups = () => {
  const [followups, setFollowups] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [stats, setStats] = useState({
    pending: 0,
    completed: 0,
    cancelled: 0,
    rescheduled: 0
  });
  
  // History popup state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientFollowups, setClientFollowups] = useState([]);
  const [followupsLoading, setFollowupsLoading] = useState(false);
  
  // Quick add followup state
  const [newFollowup, setNewFollowup] = useState({
    followup_date: new Date().toISOString().split('T')[0],
    followup_time: new Date().toTimeString().slice(0, 5),
    next_followup_date: '',
    next_followup_time: '',
    status: 'pending',
    notes: ''
  });
  const [creatingFollowup, setCreatingFollowup] = useState(false);


  const tabLabels = ['All', 'Today', 'Overdue', 'Upcoming', 'Completed'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch each endpoint separately to handle errors individually
      try {
        const followupsRes = await apiService.get('/followups');
        setFollowups(followupsRes.data?.followups || []);
      } catch (error) {
        console.error('Error fetching followups:', error);
        setFollowups([]);
        setSnackbar({
          open: true,
          message: `Failed to fetch followups: ${error.message}`,
          severity: 'error'
        });
      }

      try {
        const clientsRes = await apiService.get('/clients');
        setClients(clientsRes.data?.clients || []);
      } catch (error) {
        console.error('Error fetching clients:', error);
        setClients([]);
      }

      try {
        const usersRes = await apiService.get('/users');
        setUsers(usersRes.data?.users || []);
      } catch (error) {
        console.error('Error fetching users:', error);
        setUsers([]);
      }

      try {
        const statsRes = await apiService.get('/followups/stats');
        setStats(statsRes.data?.stats || {});
      } catch (error) {
        console.error('Error fetching stats:', error);
        setStats({});
      }

    } catch (error) {
      console.error('Unexpected error in fetchData:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const getFilteredFollowups = () => {
    // Group followups by client and get only the latest one for each client
    const clientLatestFollowups = {};
    
    followups.forEach(followup => {
      const clientId = followup.client_id;
      if (!clientLatestFollowups[clientId] || 
          new Date(followup.followup_date) > new Date(clientLatestFollowups[clientId].followup_date)) {
        clientLatestFollowups[clientId] = followup;
      }
    });
    
    // Convert to array and apply filters
    let filteredFollowups = Object.values(clientLatestFollowups);
    
    switch (currentTab) {
      case 1: // Today
        return filteredFollowups.filter(f => {
          if (!f.next_followup_date) return false;
          const isTodayDate = isToday(parseISO(f.next_followup_date));
          const isNotCompleted = f.status !== 'completed';
          return isTodayDate && isNotCompleted;
        });
        
      case 2: // Overdue
        return filteredFollowups.filter(f => {
          if (!f.next_followup_date) return false;
          const isPastDate = isPast(parseISO(f.next_followup_date));
          const isNotCompleted = f.status !== 'completed';
          return isPastDate && isNotCompleted;
        });
        
      case 3: // Upcoming
        return filteredFollowups.filter(f => {
          if (!f.next_followup_date) return false;
          const isFutureDate = isFuture(parseISO(f.next_followup_date));
          const isNotCompleted = f.status !== 'completed';
          return isFutureDate && isNotCompleted;
        });
        
      case 4: // Completed
        return filteredFollowups.filter(f => f.status === 'completed');
        
      default: // All
        return filteredFollowups;
    }
  };


  // Handle history popup
  const handleOpenHistory = async (client) => {
    setSelectedClient(client);
    setHistoryDialogOpen(true);
    setFollowupsLoading(true);
    
    try {
      const clientId = client?.client_id ?? client?.id;
      const response = await apiService.get('/followups/by-client', { client_id: clientId });
      if (response.success) {
        setClientFollowups(response.data.followups || []);
      } else {
        setClientFollowups([]);
      }
    } catch (error) {
      console.error('Error fetching client followups:', error);
      setClientFollowups([]);
    } finally {
      setFollowupsLoading(false);
    }
  };

  const handleCloseHistory = () => {
    setHistoryDialogOpen(false);
    setSelectedClient(null);
    setClientFollowups([]);
    setNewFollowup({
      followup_date: new Date().toISOString().split('T')[0],
      followup_time: new Date().toTimeString().slice(0, 5),
      next_followup_date: '',
      next_followup_time: '',
      status: 'pending',
      notes: ''
    });
  };

  // Handle quick add followup
  const handleCreateFollowup = async () => {
    if (!newFollowup.followup_date) {
      setSnackbar({
        open: true,
        message: 'Follow-up date is required',
        severity: 'error'
      });
      return;
    }

    // Validate follow-up date is not greater than today (can't follow up on a future date)
    const followupDate = new Date(newFollowup.followup_date + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (followupDate > today) {
      setSnackbar({
        open: true,
        message: 'Follow-up date cannot be greater than today',
        severity: 'error'
      });
      return;
    }

    // Validate next follow-up date is not less than today (next follow-up must be today or future)
    if (newFollowup.next_followup_date) {
      const nextFollowupDate = new Date(newFollowup.next_followup_date + 'T00:00:00');
      if (nextFollowupDate < today) {
        setSnackbar({
          open: true,
          message: 'Next follow-up date cannot be less than today',
          severity: 'error'
        });
        return;
      }
    }

    try {
      setCreatingFollowup(true);
      
      const followupData = {
        client_id: selectedClient?.client_id ?? selectedClient?.id,
        followup_date: newFollowup.followup_date,
        followup_time: newFollowup.followup_time,
        next_followup_date: newFollowup.next_followup_date || null,
        next_followup_time: newFollowup.next_followup_time || null,
        status: newFollowup.status,
        notes: newFollowup.notes
      };

      const response = await apiService.post('/followups', followupData);
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Follow-up created successfully',
          severity: 'success'
        });
        
        // Reset form
        setNewFollowup({
          followup_date: new Date().toISOString().split('T')[0],
          followup_time: new Date().toTimeString().slice(0, 5),
          next_followup_date: '',
          next_followup_time: '',
          status: 'pending',
          notes: ''
        });
        
        // Refresh followups list
        fetchData();
        
        // Refresh client followups in history
        const historyResponse = await apiService.get('/followups/by-client', {
          client_id: selectedClient?.client_id ?? selectedClient?.id,
        });
        if (historyResponse.success) {
          setClientFollowups(historyResponse.data.followups || []);
        }
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to create follow-up: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setCreatingFollowup(false);
    }
  };


  const handleMarkCompleted = async (id) => {
    try {
      await apiService.post(`/followups/${id}/complete`, { notes: 'Marked as completed' });
      setSnackbar({
        open: true,
        message: 'Followup marked as completed',
        severity: 'success'
      });
      fetchData();
    } catch (error) {
      console.error('Error marking followup as completed:', error);
      setSnackbar({
        open: true,
        message: 'Failed to mark followup as completed',
        severity: 'error'
      });
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      case 'rescheduled': return 'info';
      default: return 'default';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'call': return <PhoneIcon />;
      case 'visit': return <PersonIcon />;
      case 'appointment': return <ScheduleIcon />;
      case 'payment': return <AssignmentIcon />;
      default: return <EventIcon />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = parseISO(dateString);
    if (isToday(date)) return 'Today';
    if (isPast(date)) return format(date, 'MMM dd');
    return format(date, 'MMM dd');
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1">
            Follow-ups
          </Typography> 
        </Box>


        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            {tabLabels.map((label, index) => (
              <Tab key={index} label={label} />
            ))}
          </Tabs>
        </Paper>

        {/* Followups List */}
        <Grid container spacing={3}>
          {getFilteredFollowups().map((followup) => (
            <Grid item xs={12} md={6} lg={4} key={followup.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="h2" sx={{ flex: 1 }}>
                      {followup.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* <Chip
                        label={followup.priority}
                        color={getPriorityColor(followup.priority)}
                        size="small"
                      /> */}
                      <Chip
                        label={followup.status}
                        color={getStatusColor(followup.status)}
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Typography color="textSecondary" gutterBottom>
                    {followup.description}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {followup.client_name ||
                        `${followup.client_first_name || ''} ${followup.client_last_name || ''}`.trim() ||
                        'Unknown'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EventIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {formatDate(followup.followup_date)} at {formatTime(followup.followup_time)}
                      </Typography>
                    </Box>
                    {followup.next_followup_date ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginLeft: 2 }}>
                        <AccessAlarm sx={{ fontSize: 16, verticalAlign: 'middle'  }} /> 
                        <Typography variant="body2">
                          {formatDate(followup.next_followup_date)} at {formatTime(followup.next_followup_time)}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginLeft: 2 }}>
                        <AccessAlarm sx={{ fontSize: 16, verticalAlign: 'middle'  }} /> 
                        <Typography variant="body2" color="textSecondary">
                          Not scheduled
                        </Typography>
                      </Box>
                    )}
                  </Box>
 

                  {followup.notes && (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Notes: {followup.notes}
                    </Typography>
                  )}
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    startIcon={<ScheduleIcon />}
                    onClick={() => handleOpenHistory(followup)}
                    color="primary"
                    variant="outlined"
                  >
                    History
                  </Button>
                  {followup.status === 'pending' && (
                    <Button
                      size="small"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => handleMarkCompleted(followup.id)}
                      color="success"
                    >
                      Complete
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {getFilteredFollowups().length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="textSecondary">
              No followups found
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {currentTab === 0 ? 'No clients have follow-ups yet' : 
               currentTab === 1 ? 'No pending follow-ups for today' :
               currentTab === 2 ? 'No overdue follow-ups' :
               currentTab === 3 ? 'No upcoming follow-ups' :
               'No completed follow-ups'}
            </Typography>
          </Box>
        )}


        {/* History Dialog */}
        <Dialog
          open={historyDialogOpen}
          onClose={() => {
            setHistoryDialogOpen(false);
            setSelectedClient(null);
            setError("");
            setSuccessMessage("");
          }}
          maxWidth="md"
          fullWidth
        >
        <DialogTitle>
          <Typography variant="h6">
            Follow-ups for {selectedClient?.client_name || 'Unknown Client'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Error Message Display */}
            {error && (
              <Alert severity="error" sx={{ mb: 2, color: "error.main" }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}
            
            {/* Success Message Display */}
            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
                {successMessage}
              </Alert>
            )}
            
            {/* Row 1: Follow-up Date + Time */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Follow-up Date"
                type="date"
                value={newFollowup.followup_date}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const today = new Date().toISOString().split('T')[0];
                  
                  // Only allow dates up to today
                  if (selectedDate <= today) {
                    setNewFollowup({ ...newFollowup, followup_date: selectedDate });
                  }
                }}
                required
                error={!!error && !newFollowup.followup_date}
                helperText={error && !newFollowup.followup_date ? 'Follow-up date is required' : 'Cannot be future date'}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: (() => {
                    const today = new Date();
                    const year = today.getFullYear();
                    const month = String(today.getMonth() + 1).padStart(2, '0');
                    const day = String(today.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  })()
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-error": {
                      borderColor: "error.main",
                    },
                  },
                }}
              />
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={newFollowup.followup_time}
                onChange={(e) => setNewFollowup({ ...newFollowup, followup_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Next Follow-up Date"
                type="date"
                value={newFollowup.next_followup_date}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const today = new Date().toISOString().split('T')[0];
                  
                  // Only allow dates from today onwards
                  if (selectedDate >= today) {
                    setNewFollowup({ ...newFollowup, next_followup_date: selectedDate });
                  }
                }}
                helperText="Cannot be past date"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: new Date().toISOString().split('T')[0]
                }}
              />
              <TextField
                fullWidth
                label="Next Follow-up Time"
                type="time"
                value={newFollowup.next_followup_time}
                onChange={(e) => setNewFollowup({ ...newFollowup, next_followup_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* Row 4: Notes (full width) */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                value={newFollowup.notes}
                onChange={(e) => setNewFollowup({ ...newFollowup, notes: e.target.value })}
                multiline
                rows={3}
                placeholder="Add your comment here... (optional)"
              />
            </Box>
            {/* Row 3: Status (full width) */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                select
                label="Status"
                value={newFollowup.status}
                onChange={(e) => setNewFollowup({ ...newFollowup, status: e.target.value })}
                required
              >
                <MenuItem value="cold">Cold</MenuItem>
                <MenuItem value="warm">Warm</MenuItem>
                <MenuItem value="hot">Hot</MenuItem>
                <MenuItem value="wrong-number">Wrong-number</MenuItem>
                <MenuItem value="lost-to-competitor">Lost-to-competitor</MenuItem>
                <MenuItem value="future-contact">Future-Contact</MenuItem>
                <MenuItem value="lead-converted">Lead-Converted</MenuItem>
                <MenuItem value="not-interested">Not Interested</MenuItem>
                <MenuItem value="dnd">DND(Do not dial)</MenuItem>
              </TextField>
              <Button 
                fullWidth
                onClick={() => {
                  handleCreateFollowup();
                  handleCloseHistory();
                }}
                variant="contained"
                disabled={creatingFollowup}
              >
                {creatingFollowup ? 'Adding...' : 'Add Followup'}
              </Button>
            </Box>

          </Box>
          
          {/* Followups List */}
          <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
            Follow-up History
          </Typography>
          
          {followupsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : clientFollowups.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Notifications sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No follow-ups found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This client doesn't have any follow-ups yet
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Total Follow-ups: {clientFollowups.length}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date Followed</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Next Follow-up</TableCell>
                      <TableCell>Comment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientFollowups.map((followup) => (
                      <TableRow key={followup.id}>
                        <TableCell>
                          {new Date(followup.followup_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {followup.followup_time ? followup.followup_time.substring(0, 5) : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={followup.status}
                            color={followup.status === 'hot' ? 'error' : 
                                   followup.status === 'warm' ? 'warning' : 
                                   followup.status === 'cold' ? 'info' : 'default'}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          {followup.next_followup_date ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {new Date(followup.next_followup_date).toLocaleDateString()}
                              </Typography>
                              {followup.next_followup_time && (
                                <Typography variant="caption" color="textSecondary">
                                  at {followup.next_followup_time.substring(0, 5)}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                              Not scheduled
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {followup.notes || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Cancel</Button>
          
        </DialogActions>
      </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default Followups;
